import { CalendarIcon, PinIcon } from "@app/lib/events-admin-icons";
import { formatDateRange } from "@app/lib/events-admin-utils";
import { InvitationLayout } from "@app/components/invitation-layout";
import { formatOneworld } from "@app/components/oneworld-word";
import { CurrencySelector } from "@app/components/currency-selector";
import { SpinnerPage } from "@app/components/spinner";
import { useEventInvitationData } from "@app/hooks/use-event-invitation-data";
import { canViewPublicEventLandingPage } from "@app/lib/event-status";
import { ONEWORLD_LOGO_SRC } from "@app/lib/oneworld-branding";
import { useParams } from "react-router";

const HERO_CONTENT = {
  title: "SELECT YOUR CURRENCY",
  label: "YOU'RE INVITED!",
  eventName: "APEX Sales Summit 2026",
  tagline: "Join us and save with special oneworld member airline airfare!",
} as const;

const TAILFIN_IMAGE_SRC = `${import.meta.env.BASE_URL}images/oneworld-tailfins.png`;

const VENUE_DETAILS = {
  name: "H\u00f4tel des Bergues Geneva",
  address: "33 Quai des Bergues, Geneva 1201, Switzerland",
} as const;

const EVENT_DATE_DETAILS = {
  dateRange: "Jun 14, 2026 \u2014 Jun 17, 2026",
  label: "EVENT DATES",
} as const;

export function InvitationPage() {
  const { eventId } = useParams();

  const { event, landingPage, hasLoadError, isLoading } =
    useEventInvitationData(eventId, { authMode: "public" });

  // Show a loader instead of the default placeholder content while the first
  // load settles, so a copied link opened in a new tab never flashes the
  // hardcoded sample event before the real event/landing page resolves.
  if (isLoading) {
    return <SpinnerPage height="100dvh" />;
  }

  if (hasLoadError || !event || !canViewPublicEventLandingPage(event.status)) {
    return (
      <section className="events-page events-page--stack events-page--home">
        <div className="events-empty-state">
          <h1 className="events-page__title">
            This event page isn&apos;t available
          </h1>
          <p className="events-page__subtitle">
            The link may be expired or the event may no longer be accepting
            registrations.
          </p>
        </div>
      </section>
    );
  }

  const eventName = event?.name || HERO_CONTENT.eventName;
  const landingTitle = landingPage?.title?.trim() || eventName;
  const landingSubtitle = landingPage?.subtitle?.trim() || HERO_CONTENT.tagline;

  const eventDateRange =
    event?.startDate && event?.endDate
      ? formatDateRange(event.startDate, event.endDate)
      : EVENT_DATE_DETAILS.dateRange;

  const descriptionText =
    landingPage?.description?.trim() || event?.description?.trim() || "";

  const venueName =
    event?.venue?.trim() || event?.destination?.trim() || VENUE_DETAILS.name;

  const venueAddress = event?.venueAddress?.trim() || "";

  const heroCity =
    event?.destination?.trim() ||
    event?.venueAddress?.trim() ||
    VENUE_DETAILS.address;

  const bookingLinks = landingPage?.bookingLinks ?? [];

  return (
    <InvitationLayout
      sectionTitle={HERO_CONTENT.title}
      heroLabel={HERO_CONTENT.label}
      heroTitle={
        <h1 className="events-invitation-hero__title">{landingTitle}</h1>
      }
      heroTagline={
        <p className="events-invitation-hero__tagline">
          {formatOneworld(landingSubtitle)}
        </p>
      }
      heroAfterTagline={
        <>
          <span className="events-invitation-hero__city">
            <PinIcon className="events-invitation-hero__pin" />
            {heroCity}
          </span>

          <hr className="events-invitation-hero__divider" />

          <CurrencySelector bookingLinks={bookingLinks} />
        </>
      }
      heroFooter={
        <div
          className="events-invitation-tailfins"
          role="img"
          aria-label="oneworld member airlines"
        >
          <img
            src={TAILFIN_IMAGE_SRC}
            alt=""
            aria-hidden="true"
            width="3040"
            height="183"
            decoding="async"
          />
        </div>
      }
      description={{
        heading: (
          <h2 className="events-invitation-details__heading">Description</h2>
        ),
        body: (
          <p className="events-invitation-details__text">{descriptionText}</p>
        ),
      }}
      venue={{
        heading: <h2 className="events-invitation-details__heading">Venue</h2>,
        body: (
          <div className="events-invitation-details__item">
            <span className="events-invitation-details__icon events-invitation-details__icon--venue">
              <PinIcon />
            </span>
            <div>
              <p className="events-invitation-details__item-title">
                {venueName}
              </p>
              {venueAddress ? (
                <p className="events-invitation-details__item-subtitle">
                  {venueAddress}
                </p>
              ) : null}
            </div>
          </div>
        ),
      }}
      dates={{
        heading: (
          <h2 className="events-invitation-details__heading">Event Dates</h2>
        ),
        body: (
          <div className="events-invitation-details__item">
            <span className="events-invitation-details__icon events-invitation-details__icon--dates">
              <CalendarIcon />
            </span>
            <div>
              <p className="events-invitation-details__item-title">
                {eventDateRange}
              </p>
              <p className="events-invitation-details__item-label">
                {EVENT_DATE_DETAILS.label}
              </p>
            </div>
          </div>
        ),
      }}
      footer={
        <footer className="events-invitation-footer">
          <span className="events-shell__logo events-invitation-footer__logo">
            <img
              src={ONEWORLD_LOGO_SRC}
              alt="oneworld"
              className="events-shell__logo-image"
              decoding="async"
              referrerPolicy="no-referrer"
            />
          </span>
          <p className="events-invitation-footer__tagline">
            INVITATION POWERED BY{" "}
            <span className="events-invitation-footer__tagline-brand">ITS</span>{" "}
            GROUPS
          </p>
          <p className="events-invitation-footer__message">
            This email was sent to your group travelers as part of the{" "}
            {eventName} coordination.
          </p>
          <p className="events-invitation-footer__copyright">
            &copy;2026 {formatOneworld("ONEWORLD MANAGEMENT COMPANY, INC.")}
          </p>
        </footer>
      }
    />
  );
}
